import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, limit, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import UserNav from '../../components/navbar/user-nav';
import AdminOnlySidebar from '../../components/admin-only-sidebar';
import PageTitle from '../../components/page-title';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import { usePermissions } from '../../hooks/usePermissions';

interface SyncLogEntry {
  id: string;
  start_time: any;
  end_time: any;
  duration_ms: number;
  total_fetched: number;
  created: number;
  updated: number;
  skipped: number;
  deactivated: number;
  errors: number;
  agencies_created: number;
  agents_created: number;
  dry_run: boolean;
  timestamp: any;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAdmin } = usePermissions();
  const [show, setShow] = useState(false);
  const [estadisticas, setEstadisticas] = useState({
    afiliados: 0,
    asociados: 0,
    propiedadesMls: 0,
    propiedadesManuales: 0,
    propiedadesSinAgencia: 0,
    propiedadesSinAgente: 0,
    agencias: 0,
    desarrollos: 0,
    talleres: 0,
    agentesPendientes: 0,
    agenciasPendientes: 0,
    usuariosSinFirebase: 0
  });
  const [cargandoStats, setCargandoStats] = useState(true);
  const [lastSync, setLastSync] = useState<SyncLogEntry | null>(null);
  const [eliminandoManuales, setEliminandoManuales] = useState(false);
  const [manualesEliminadas, setManualesEliminadas] = useState(0);

  const formatDuration = (ms: number) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeAgo = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `hace ${diffMins} min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `hace ${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    return `hace ${diffDays}d`;
  };

  useEffect(() => {
    const cargarEstadisticas = async () => {
      if (!isAdmin()) {
        setCargandoStats(false);
        return;
      }

      try {
        // Cargar afiliados
        const afiliadosSnap = await getDocs(query(collection(db, 'asesor'), where('activo', '==', true)));

        // Cargar asociados
        const asociadosSnap = await getDocs(query(collection(db, 'asociado'), where('activo', '==', true)));

        // Cargar propiedades MLS activas
        const propMlsSnap = await getDocs(
          query(collection(db, 'propiedades'), where('fuente', '==', 'flexmls'), where('estado', '==', 'A'))
        );
        const propMls = propMlsSnap.docs.map(d => d.data());

        // Contar propiedades MLS sin agencia
        const sinAgencia = propMls.filter(p =>
          !p.inmobiliaria_id && !p.inmobiliaria_doc_id && !p.agencia_info?.inmobiliaria_id
        ).length;

        // Contar propiedades MLS sin agente asignado
        const sinAgente = propMls.filter(p =>
          !p.asesor_doc_ids || !Array.isArray(p.asesor_doc_ids) || p.asesor_doc_ids.length === 0
        ).length;

        // Contar propiedades manuales (sin fuente o fuente != flexmls)
        // Sprint 0 (F8-004): limit explícito para evitar lectura ilimitada.
        const todasSnap = await getDocs(query(collection(db, 'propiedades'), limit(5000)));
        const manuales = todasSnap.docs.filter(d => {
          const data = d.data();
          return data.fuente !== 'flexmls';
        }).length;

        // Cargar agencias
        const agenciasSnap = await getDocs(query(collection(db, 'inmobiliaria'), limit(1000)));

        // Cargar desarrollos
        const desarrollosSnap = await getDocs(query(collection(db, 'proyectos'), limit(1000)));

        // Cargar talleres
        const talleresSnap = await getDocs(query(collection(db, 'taller'), limit(500)));

        // Agentes pendientes de verificar
        const agentesPendSnap = await getDocs(
          query(collection(db, 'asesor'), where('verificacion_pendiente', '==', true))
        );

        // Agencias pendientes de verificar
        const agenciasPendSnap = await getDocs(
          query(collection(db, 'inmobiliaria'), where('verificacion_pendiente', '==', true))
        );

        // Contar usuarios sin Firebase Auth
        let sinFirebase = 0;
        afiliadosSnap.forEach(doc => {
          if (!doc.data().firebase_uid) sinFirebase++;
        });
        asociadosSnap.forEach(doc => {
          if (!doc.data().firebase_uid) sinFirebase++;
        });

        // Cargar ultimo sync
        const syncSnap = await getDocs(
          query(collection(db, 'mls_sync_log'), orderBy('timestamp', 'desc'), limit(1))
        );
        if (!syncSnap.empty) {
          const syncData = syncSnap.docs[0];
          setLastSync({ id: syncData.id, ...syncData.data() } as SyncLogEntry);
        }

        setEstadisticas({
          afiliados: afiliadosSnap.size,
          asociados: asociadosSnap.size,
          propiedadesMls: propMlsSnap.size,
          propiedadesManuales: manuales,
          propiedadesSinAgencia: sinAgencia,
          propiedadesSinAgente: sinAgente,
          agencias: agenciasSnap.size,
          desarrollos: desarrollosSnap.size,
          talleres: talleresSnap.size,
          agentesPendientes: agentesPendSnap.size,
          agenciasPendientes: agenciasPendSnap.size,
          usuariosSinFirebase: sinFirebase
        });
      } catch (error) {
        console.error('Error cargando estadisticas:', error);
      } finally {
        setCargandoStats(false);
      }
    };

    cargarEstadisticas();
  }, [isAdmin]);

  const eliminarPropiedadesManuales = async () => {
    if (!window.confirm(
      `Se eliminarán ${estadisticas.propiedadesManuales} propiedades que NO provienen del MLS.\n\nEsta acción es irreversible. ¿Continuar?`
    )) return;

    setEliminandoManuales(true);
    try {
      const todasSnap = await getDocs(query(collection(db, 'propiedades'), limit(5000)));
      let eliminadas = 0;
      for (const docSnap of todasSnap.docs) {
        const data = docSnap.data();
        if (data.fuente !== 'flexmls') {
          await deleteDoc(doc(db, 'propiedades', docSnap.id));
          eliminadas++;
        }
      }
      setManualesEliminadas(eliminadas);
      setEstadisticas(prev => ({ ...prev, propiedadesManuales: 0 }));
    } catch (error) {
      console.error('Error eliminando propiedades manuales:', error);
      alert('Error al eliminar. Revisa la consola.');
    } finally {
      setEliminandoManuales(false);
    }
  };


  const getWelcomeMessage = () => {
    if (!user) return "Cargando...";
    if (isAdmin()) return `Bienvenido Administrador: ${user.nombre}`;
    return `Bienvenido ${user.rol}: ${user.nombre}`;
  };

  return (
    <>
      <UserNav nombreUsuario={user?.nombre} nombreInmobiliaria={user?.inmobiliaria_nombre} />

      <PageTitle
        title="Panel de Administracion"
        subtitle="Gestiona todos los recursos del sistema"
      />

      <section className="py-4">
        <div className="container-fluid px-4">
          <Link to="#" className="btn btn-dark full-width mb-4 d-md-none" onClick={() => setShow(!show)}>
            Dashboard Navigation<i className="fa-solid fa-bars ms-2"></i>
          </Link>
          <div className="row">
            <div className="col-lg-3 col-md-12">
              <AdminOnlySidebar show={show} setShow={setShow} />
            </div>
            <div className="col-lg-9 col-md-12">
              {/* Mensaje de bienvenida */}
              <div className="alert alert-info mb-4">
                <h4><i className="fa fa-dashboard me-2"></i>{getWelcomeMessage()}</h4>
                {user?.inmobiliaria_nombre && (
                  <p className="mb-0">
                    <i className="fa fa-building me-2"></i>
                    Inmobiliaria: <strong>{user.inmobiliaria_nombre}</strong>
                  </p>
                )}
              </div>

              {/* Resumen del sistema */}
              {isAdmin() && (
                <>
                  {cargandoStats ? (
                    <div className="text-center py-3">
                      <div className="spinner-border text-primary" role="status" />
                    </div>
                  ) : (
                    <>
                      {/* Sync MLS compacto */}
                      {lastSync && (
                        <div className="card border-0 shadow-sm mb-3" onClick={() => navigate('/admin-mls-sync')} style={{ cursor: 'pointer' }}>
                          <div className="card-body py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center">
                              <small>
                                <i className="fa-solid fa-rotate me-1 text-primary"></i>
                                <strong>Sync MLS</strong>
                                <span className={`badge ms-2 ${lastSync.errors > 0 ? 'bg-warning' : 'bg-success'}`} style={{ fontSize: '0.65rem' }}>
                                  {lastSync.errors > 0 ? 'Errores' : 'OK'}
                                </span>
                              </small>
                              <small className="text-muted">{getTimeAgo(lastSync.timestamp)}</small>
                            </div>
                            <div className="d-flex gap-3 mt-1" style={{ fontSize: '0.8rem' }}>
                              <span className="text-success"><strong>{lastSync.created}</strong> nuevas</span>
                              <span className="text-primary"><strong>{lastSync.updated}</strong> actual.</span>
                              <span className="text-warning"><strong>{lastSync.deactivated}</strong> desact.</span>
                              {lastSync.errors > 0 && <span className="text-danger"><strong>{lastSync.errors}</strong> errores</span>}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Alertas - solo si hay pendientes */}
                      {(estadisticas.propiedadesSinAgente > 0 || estadisticas.agentesPendientes > 0 || estadisticas.agenciasPendientes > 0 || estadisticas.propiedadesManuales > 0) && (
                        <div className="card border-warning mb-3">
                          <div className="card-body py-2 px-3">
                            <small><i className="fas fa-exclamation-triangle text-warning me-1"></i><strong>Pendientes:</strong></small>
                            <div className="d-flex gap-2 flex-wrap mt-1" style={{ fontSize: '0.8rem' }}>
                              {estadisticas.agentesPendientes > 0 && (
                                <span style={{ cursor: 'pointer' }} onClick={() => navigate('/admin-afiliado')}>
                                  <strong className="text-warning">{estadisticas.agentesPendientes}</strong> agentes
                                </span>
                              )}
                              {estadisticas.agenciasPendientes > 0 && (
                                <span style={{ cursor: 'pointer' }} onClick={() => navigate('/admin-inm')}>
                                  <strong className="text-warning">{estadisticas.agenciasPendientes}</strong> agencias
                                </span>
                              )}
                              {estadisticas.propiedadesSinAgente > 0 && (
                                <span><strong className="text-danger">{estadisticas.propiedadesSinAgente}</strong> sin agente</span>
                              )}
                              {estadisticas.propiedadesManuales > 0 && (
                                <span>
                                  <strong className="text-secondary">{estadisticas.propiedadesManuales}</strong> manuales
                                  <button className="btn btn-outline-danger ms-1 py-0" style={{ fontSize: '0.7rem' }} onClick={eliminarPropiedadesManuales} disabled={eliminandoManuales}>
                                    {eliminandoManuales ? '...' : 'Eliminar'}
                                  </button>
                                </span>
                              )}
                            </div>
                            {manualesEliminadas > 0 && (
                              <small className="text-success d-block mt-1"><i className="fas fa-check me-1"></i>{manualesEliminadas} eliminadas</small>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Accesos rapidos con stats */}
              <div className="row g-2">
                {[
                  { label: 'Propiedades MLS', count: estadisticas.propiedadesMls, path: '/admin-property', icon: 'fas fa-home', color: '#0dcaf0' },
                  { label: 'Agencias', count: estadisticas.agencias, path: '/admin-inm', icon: 'fas fa-building', color: '#ffc107' },
                  { label: 'Afiliados', count: estadisticas.afiliados, path: '/admin-afiliado', icon: 'fas fa-users', color: '#0d6efd' },
                  { label: 'Asociados', count: estadisticas.asociados, path: '/admin-asociado', icon: 'fas fa-user-tie', color: '#198754' },
                  { label: 'Cursos', count: estadisticas.talleres, path: '/admin-talleres', icon: 'fas fa-graduation-cap', color: '#6c757d' },
                  { label: 'Resenas', count: null, path: '/aprobar-resena', icon: 'fas fa-star', color: '#212529' },
                ].map((item, i) => (
                  <div className="col-4 col-md-2" key={i}>
                    <div
                      className="card shadow-sm text-center h-100"
                      style={{ cursor: 'pointer', borderTop: `3px solid ${item.color}` }}
                      onClick={() => navigate(item.path)}
                    >
                      <div className="card-body py-2 px-1">
                        {item.count !== null && (
                          <h4 className="mb-0" style={{ color: item.color }}>{item.count}</h4>
                        )}
                        {item.count === null && (
                          <i className={`${item.icon} fa-lg mb-1`} style={{ color: item.color }}></i>
                        )}
                        <small className="d-block text-muted" style={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{item.label}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
